import { Centrifuge, UnauthorizedError } from 'centrifuge'
import { chatApi, errorStatus } from '../api/chatApi'
import type { ChatEvent } from '../types'

/**
 * Messages 的实时连接（Centrifugo）。整个页面只开一条，谁要用谁 acquire，全都 release 了就断开。
 * 与 main-mobile src/features/chat/realtime/chatRealtime.ts 同一套做法：
 *
 * - 地址与 token 每次都向后端要（GET /system/chat/realtime-token）：地址不写死在前端，
 *   token 一小时过期，centrifuge-js 在过期前经 getToken 再要一张。
 * - 后端没配 Centrifugo（enabled=false）时状态是 "disabled"：调用方改成轮询，消息照样能收发。
 * - 连上后服务端自动把这条连接订阅到个人频道 user:#<userId>，这里不发 subscribe。
 * - 断线重连、网络恢复都发一次 "resync"：调用方回 REST 补拉。数据库才是真相源，
 *   Centrifugo 的历史只是热缓存，补拉比依赖恢复更稳。
 * - 浏览器标签页切到后台不断开（顶栏未读数要跟着走）；离线时 centrifuge-js 自己退避重连。
 */

export type RealtimeStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'disabled'

/** 起不来（不是被拒）时多久再试一次 */
const RETRY_START_MS = 30_000

type EventListener = (event: ChatEvent) => void
type VoidListener = () => void
type StatusListener = (status: RealtimeStatus) => void

async function fetchToken() {
  try {
    return await chatApi.realtimeToken()
  } catch (err) {
    const status = errorStatus(err)
    if (status === 401 || status === 403) throw new UnauthorizedError('chat realtime token refused')
    throw err
  }
}

export class ChatRealtime {
  private client: Centrifuge | null = null
  private refs = 0
  private status: RealtimeStatus = 'idle'
  private everConnected = false
  private starting = false
  /** 起连接失败（取 token 网络错误等）后的重试定时器 */
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private readonly events = new Set<EventListener>()
  private readonly resyncs = new Set<VoidListener>()
  private readonly statuses = new Set<StatusListener>()

  /** 开始用；返回的函数是 release。 */
  acquire(): () => void {
    this.refs += 1
    if (this.refs === 1) {
      window.addEventListener('online', this.onOnline)
      void this.start()
    }
    let released = false
    return () => {
      if (released) return
      released = true
      this.refs -= 1
      if (this.refs === 0) {
        window.removeEventListener('online', this.onOnline)
        this.stop('idle')
      }
    }
  }

  onEvent(fn: EventListener): () => void {
    this.events.add(fn)
    return () => { this.events.delete(fn) }
  }

  onResync(fn: VoidListener): () => void {
    this.resyncs.add(fn)
    return () => { this.resyncs.delete(fn) }
  }

  onStatus(fn: StatusListener): () => void {
    this.statuses.add(fn)
    fn(this.status)
    return () => { this.statuses.delete(fn) }
  }

  getStatus(): RealtimeStatus {
    return this.status
  }

  private setStatus(next: RealtimeStatus) {
    if (this.status === next) return
    this.status = next
    for (const fn of this.statuses) fn(next)
  }

  private emitResync() {
    for (const fn of this.resyncs) fn()
  }

  private readonly onOnline = () => {
    // 没有连接（上次起不来）就再起一次；连着的由 centrifuge-js 自己重连，connected 时补拉
    if (!this.client && this.status !== 'disabled') void this.start()
    this.emitResync()
  }

  private async start() {
    if (this.client || this.starting) return
    this.starting = true
    this.setStatus('connecting')
    try {
      const info = await fetchToken()
      if (this.refs === 0) {
        this.setStatus('idle')
        return
      }
      if (!info.enabled || !info.url || !info.token) {
        this.setStatus('disabled')
        return
      }
      const channel = info.channel
      const client = new Centrifuge(info.url, {
        token: info.token,
        getToken: async () => {
          const next = await fetchToken()
          if (!next.enabled || !next.token) throw new UnauthorizedError('chat realtime disabled')
          return next.token
        },
        minReconnectDelay: 1000,
        maxReconnectDelay: 20000,
      })
      client.on('connecting', () => this.setStatus('connecting'))
      client.on('connected', () => {
        this.setStatus('connected')
        if (this.everConnected) this.emitResync()
        this.everConnected = true
      })
      client.on('disconnected', () => this.setStatus('disconnected'))
      client.on('publication', (ctx) => {
        if (channel && ctx.channel !== channel) return
        const data = ctx.data as ChatEvent | undefined
        if (!data || typeof data !== 'object' || !('type' in data)) return
        for (const fn of this.events) fn(data)
      })
      client.on('error', (ctx) => console.warn('[chat realtime] error', ctx?.error?.message))
      this.client = client
      client.connect()
    } catch (err) {
      console.warn('[chat realtime] cannot start', (err as Error)?.message)
      if (err instanceof UnauthorizedError) {
        this.setStatus('disabled')
      } else {
        this.setStatus('disconnected')
        this.scheduleRetry()
      }
    } finally {
      this.starting = false
    }
  }

  private scheduleRetry() {
    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      if (this.refs > 0 && !this.client) void this.start()
    }, RETRY_START_MS)
  }

  private stop(next: RealtimeStatus) {
    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.retryTimer = null
    const client = this.client
    this.client = null
    this.everConnected = false
    if (client) {
      client.removeAllListeners()
      client.disconnect()
    }
    this.setStatus(next)
  }
}

export const chatRealtime = new ChatRealtime()
